import { appColors } from '@/src/theme/foundations/colors'
import { Button, type ButtonProps, type FlexProps, type PlacementWithLogical, Tooltip, forwardRef } from '@chakra-ui/react'
import type { ReactElement } from 'react'
import { BaseText, type TextProps } from '../BaseText'

export interface BaseButtonProps extends Omit<ButtonProps, 'variant' | 'disabled' | 'colorScheme' | 'size'> {
  variant?: 'primary' | 'secondary' | 'tertiary'
  text?: string
  textAlignment?: FlexProps['justifyContent']
  colorScheme?: 'green' | 'red' | 'yellow' | 'negative'
  size?: 'big' | 'medium' | 'small'
  label?: string
  onClick?: React.MouseEventHandler<HTMLButtonElement>
  iconLeft?: ReactElement<any>
  iconRight?: ReactElement<any>
  tooltip?: string
  tooltipPlacement?: PlacementWithLogical
  buttonTextProps?: Omit<TextProps, 'children'>
}

/**
 * Not finished yet. TODO: V Finish with align figma design ticket
 */
export const BaseButton = forwardRef<BaseButtonProps, 'button'>((props, ref) => {
  const {
    colorScheme,
    variant,
    size,
    text,
    textAlignment,
    label,
    iconLeft,
    iconRight,
    onClick,
    tooltip, // Extracting tooltip prop
    tooltipPlacement,
    children,
    buttonTextProps = {},
    ...rest
  } = props

  let height = '32px'
  let textStyle: 'base/text-base Medium' | 'sm/text-sm Medium' | 'xs/text-xs Medium' = 'sm/text-sm Medium'
  let bg = appColors.primary
  let bgActive = appColors.accentPrimary
  let bgHover = appColors.accentPrimary
  let border: string | undefined = 'none'
  let padding = '6px 12px'
  let textColor = appColors.primaryForeground
  let textColorActive = ''
  let textColorHover = ''
  let boxShadow: string | undefined = undefined
  ;('')

  if (size === 'small') {
    height = '32px'
    textStyle = 'xs/text-xs Medium'
  }

  if (size === 'big') {
    height = '40px'
    textStyle = 'base/text-base Medium'
    padding = '6px 12px'
  }

  if (variant === 'primary') {
    border = `1px solid ${appColors.accentPrimary}`
    boxShadow = '0px 4px 6px -1px rgba(0, 0, 0, 0.10), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)'
  }

  if (variant === 'secondary') {
    textColor = appColors.foreground
    bg = appColors.background
    border = `1px solid ${appColors.border}`
    bgHover = appColors.muted
    bgActive = appColors.muted
    textColorActive = appColors.primary
  }

  if (variant === 'tertiary') {
    textColor = appColors.foreground
    textColorActive = appColors.primary
    bg = 'transparent'
    bgHover = 'transparent'
    bgActive = 'transparent'
    if (colorScheme === 'red') {
      bgActive = appColors.status.error[50]
    }

    textColorHover = appColors.primary
  }

  if (colorScheme === 'red') {
    textColor = appColors.negative
    bg = appColors.red[50]
    bgHover = appColors.red[100]
    border = `1px solid ${appColors.red[400]}`
    bgActive = appColors.red[300]
    textColorActive = ''
  }

  if (colorScheme === 'yellow') {
    textColor = appColors.gray[900]
    bg = appColors.yellow[400]
    bgHover = appColors.yellow[200]
    border = `1px solid ${appColors.border}`
    bgActive = appColors.yellow[100]
    textColorActive = ''
  }
  if (colorScheme === 'negative') {
    textColor = appColors.white
    bg = appColors.negative
    bgHover = appColors.negative
    border = `1px solid ${appColors.border}`
    bgActive = appColors.negative
    textColorActive = ''
  }

  const button = (
    <Button
      ref={ref}
      color={textColor}
      leftIcon={iconLeft}
      iconSpacing={!children && !text ? 'unset' : undefined}
      rightIcon={iconRight}
      minW={'fit-content'}
      height={height}
      cursor={'pointer'}
      onClick={onClick}
      bg={bg}
      alignItems={'center'}
      borderRadius={'8px'}
      border={border}
      padding={padding}
      _hover={{ bg: bgHover, boxShadow: boxShadow, color: textColorHover }}
      _active={{ bg: bgActive, textColor: textColorActive, transform: 'translateY(1px)' }}
      _focusVisible={{ border: 'none' }}
      transition='all 0.1s ease-in-out'
      variant={undefined}
      {...rest}
    >
      <BaseText
        textStyle={textStyle}
        alignItems={'center'}
        asBox={true}
        display={'flex'}
        w='full'
        justifyContent={textAlignment ?? 'center'}
        {...buttonTextProps}
      >
        {text}
        {children}
      </BaseText>
    </Button>
  )

  return tooltip ? (
    <Tooltip label={tooltip} bg='white' color='gray.800' fontWeight='normal' borderRadius='base' hasArrow placement={tooltipPlacement}>
      {button}
    </Tooltip>
  ) : (
    button
  )
})

BaseButton.displayName = 'BaseButton'
